import produce from "immer"
import { IDLE, ASC } from "const"
import {
    CcReviewReviewItem,
    CcReviewReviewItemState,
    CcReviewReviewHistoryCreate,
} from "server/models"
import { InferValueTypes } from "client/utils"
import * as types from "./types"
import * as actions from "./actions"

type ActionTypes = ReturnType<InferValueTypes<typeof actions>>

export const initReviewReviewItem: CcReviewReviewItem = {
    id: null,
    projectId: null,
    fileId: null,
    versionId: null,
    remark: "",
    page: "",
    typeId: null,
    creator: null,
}

export const initReviewReviewItemState: CcReviewReviewItemState = {
    itemId: null,
    reviewReviewRevisionId: null,
    levelId: null,
    verifierId: null,
    settings: null,
}

export const initReviewReviewHistory: CcReviewReviewHistoryCreate = {
    itemId: null,
    reviewReviewRevisionId: null,
    parentId: null,
    prevLevelId: null,
    prevUserId: null,
    nextLevelId: null,
    nextUserId: null,
    responseId: null,
    remark: null,
    nextRevision: null,
    prevStatus: null,
    nextStatus: null,
    workflow: null,
    systemMessage: true,
    editorId: null,
}

const initCcReviewReviewState: types.CcReviewReviewState = {
    // list
    tableClass: "review-review-table",
    showColumnSettings: false,

    itemList: {},
    itemIds: [],

    revisionList: {},
    revisionActiveList: {},

    itemStateList: {},
    itemHistoryList: {},
    tempAnswerList: {},

    selected: [],

    sortColumn: "createdAt",
    sortOrder: ASC,

    loadState: IDLE,
    reloadList: true,

    // popup menu
    active: null,
    menuShow: false,
    menuTarget: null,
    showConfirmDelete: false,

    // view history and edit item
    itemDetails: initReviewReviewItem,
    itemState: initReviewReviewItemState,
    itemHistory: [],
    tempAnswers: {},
    tempAnswerHistory: null,
    reloadAnswer: true,
    reloadEdit: true,
}

const ccReviewReviewReducer = produce((
    draft: types.CcReviewReviewState = initCcReviewReviewState,
    action: ActionTypes = null,
): types.CcReviewReviewState => {
    switch (action.type) {
        // list
        case types.CC_REVIEW_REVIEW_COLUMN_SETTINGS:
            draft.showColumnSettings = action.show
            return draft

        case types.CC_REVIEW_REVIEW_ITEM_LIST:
            draft.itemList = action.itemList
            return draft

        case types.CC_REVIEW_REVIEW_ITEM_IDS:
            draft.itemIds = action.itemIds
            return draft

        case types.CC_REVIEW_REVIEW_REVISION_LIST:
            draft.revisionList = {
                ...draft.revisionList,
                [action.itemId]: action.revisions,
            }
            return draft

        case types.CC_REVIEW_REVIEW_REVISION_ACTIVE_LIST:
            draft.revisionActiveList = {
                ...draft.revisionActiveList,
                [action.itemId]: action.revision,
            }
            return draft

        case types.CC_REVIEW_REVIEW_ITEM_STATE_LIST:
            draft.itemStateList = {
                ...draft.itemStateList,
                [action.itemId]: action.state,
            }
            return draft

        case types.CC_REVIEW_REVIEW_ITEM_HISTORY_LIST:
            draft.itemHistoryList = {
                ...draft.itemHistoryList,
                [action.itemId]: action.itemHistoryList,
            }
            return draft

        case types.CC_REVIEW_REVIEW_TEMP_ANSWER_LIST:
            draft.tempAnswerList = {
                ...draft.tempAnswerList,
                [action.itemId]: action.tempAnswers,
            }
            return draft

        case types.CC_REVIEW_REVIEW_SELECTED:
            draft.selected = action.selected
            return draft

        case types.CC_REVIEW_REVIEW_SORT:
            draft.sortColumn = action.sortColumn
            draft.sortOrder = action.sortOrder
            return draft

        case types.CC_REVIEW_REVIEW_LOAD_STATE:
            draft.loadState = action.state
            return draft

        case types.CC_REVIEW_REVIEW_RELOAD_LIST:
            draft.reloadList = action.value
            return draft

        // popup menu
        case types.CC_REVIEW_REVIEW_ACTIVE:
            draft.active = action.active
            return draft

        case types.CC_REVIEW_REVIEW_POPUP_MENU_SHOW:
            draft.menuShow = action.show
            return draft

        case types.CC_REVIEW_REVIEW_POPUP_MENU_TARGET:
            draft.menuTarget = action.target
            return draft

        case types.CC_REVIEW_REVIEW_SHOW_CONFIRM_DELETE:
            draft.showConfirmDelete = action.show
            return draft

        // view history and edit item
        case types.CC_REVIEW_REVIEW_ITEM_DETAILS:
            draft.itemDetails = action.details
            return draft

        case types.CC_REVIEW_REVIEW_ITEM_STATE:
            draft.itemState = action.state
            return draft

        case types.CC_REVIEW_REVIEW_ITEM_HISTORY:
            draft.itemHistory = action.history
            return draft

        case types.CC_REVIEW_REVIEW_TEMP_ANSWERS:
            draft.tempAnswers = action.answers
            return draft

        case types.CC_REVIEW_REVIEW_TEMP_ANSWER_HISTORY:
            draft.tempAnswerHistory = action.history
            return draft

        case types.CC_REVIEW_REVIEW_RELOAD_ANSWER:
            draft.reloadAnswer = action.value
            return draft

        case types.CC_REVIEW_REVIEW_RELOAD_EDIT:
            draft.reloadEdit = action.value
            return draft

        default:
            return draft
    }
})
export default ccReviewReviewReducer
